// ─────────────────────────────────────────────────────────────────────────────
// autoDaySpec — the ONE canonical "My Day — automatic" specification.
//
// Historically this lived in THREE hand-copied places (src/lib/autoDay.js, the
// protocol-dispatcher edge function, the protocol-calendar-feed edge function)
// and they drifted (the dispatcher silently dropped a step). This module is the
// single source of truth. Everything downstream derives from it:
//   • src/lib/autoDay.js imports AUTO_DAY_SPEC from here.
//   • The two Deno edge functions cannot import from src/, so an EXACT mirror
//     lives at supabase/functions/_shared/auto-day-spec.ts. The array literals
//     in both files are byte-for-byte identical and a build/test-time assertion
//     (src/lib/autoDaySpec.identity.test.js) reads both files and fails the
//     build if they ever diverge — keys, levels, offsets, guidance and the
//     traffic factors are all compared.
//
// Per-consumer variation is expressed as a FILTER over this one spec, never as
// a separate copy:
//   • dispatchableSteps()  → push/alarm steps the dispatcher pushes (drops the
//                            silent route_start).
//   • feedSteps()          → every step (the calendar shows the full day).
//
// Offsets are LINEAR in travel and prep, so each is stored as three integer
// coefficients { k, travel, prep } meaning:
//       offsetMinutes = k + travel * travelMinutes + prep * prepMinutes
// and the fire minute is routeStart + offsetMinutes. Keeping the offset as data
// (not a function) is what lets the JS and TS specs be compared literally.
//
// KEYS ARE STABLE IDENTIFIERS. They key protocol_step_events.step_key rows,
// localStorage dismissal guards and calendar event UIDs. NEVER rename them.
// ─────────────────────────────────────────────────────────────────────────────

export const AUTO_DAY_SPEC = [
  {
    "key": "auto:wake",
    "title": "Wake up",
    "level": "alarm",
    "offset": { "k": -15, "travel": -1, "prep": -1 },
    "guidance": "Enough time to get ready without rushing, drive in and still be 15 minutes early. A calm first hour sets up the whole day."
  },
  {
    "key": "auto:leave",
    "title": "Leave home",
    "level": "push",
    "offset": { "k": -15, "travel": -1, "prep": 0 },
    "guidance": "Head out now and traffic can’t make you late — this keeps your 15-minute buffer at the depot."
  },
  {
    "key": "auto:arrive",
    "title": "Arrive at the depot — 15 minutes early",
    "level": "alarm",
    "offset": { "k": -15, "travel": 0, "prep": 0 },
    "guidance": "Walk in composed, not chasing the clock. Check in with the team so you know about anything unusual before loading."
  },
  {
    "key": "auto:samsara",
    "title": "Walk-around check",
    "level": "push",
    "offset": { "k": -10, "travel": 0, "prep": 0 },
    "guidance": "Complete your vehicle walk-around in the contractor’s Samsara app before departure — that app is where the check lives."
  },
  {
    "key": "auto:route_start",
    "title": "Route start",
    "level": "silent",
    "offset": { "k": 0, "travel": 0, "prep": 0 },
    "guidance": "Load checked, navigation set, first drop reviewed. One stop at a time."
  }
];

// Richer, gentler guidance shown to a subcontractor in their first few route
// days (see src/lib/myDayInsights.js#isFirstWeek). Same keys; swapped in by the
// UI/dispatcher — never a stored flag. Keys absent here fall back to AUTO_DAY_SPEC.
export const AUTO_DAY_FIRST_WEEK_GUIDANCE = {
  "auto:wake": "First route days feel earlier than they are. This alarm already builds in your get-ready time, the drive and a 15-minute buffer — get up when it rings and you’re never chasing the clock.",
  "auto:leave": "Leave now, even if it feels early. Arriving with time to spare beats hitting traffic and starting the day behind. It gets easier once the routine is yours.",
  "auto:arrive": "Aim to walk in 15 minutes early. Say hello, find your van, ask anything you’re unsure about — nobody minds questions in the first week.",
  "auto:samsara": "Do the walk-around in the contractor’s Samsara app before you pull off. Take your time on the first few — it becomes second nature fast.",
  "auto:route_start": "First drop reviewed, nav set, load checked. One stop at a time — steady beats rushed."
};

// Conservative, documented time-of-day multipliers over the free-flow OSRM
// driving estimate, indexed by the LOCAL hour the driver leaves home (0–23).
// OSRM's public "driving" profile is free-flow (no live traffic), so these only
// ever ADD time (every factor ≥ 1.0) — we never promise a faster drive than the
// map, and a hand-entered ("manual") travel time is never touched (see
// src/lib/trafficModel.js). Morning/evening peaks are heaviest. This is an
// estimate, labelled as such in the UI.
export const TRAFFIC_FACTORS = [
  1.00, 1.00, 1.00, 1.00, 1.00, 1.05,
  1.15, 1.25, 1.30, 1.20, 1.10, 1.05,
  1.05, 1.05, 1.08, 1.15, 1.25, 1.30,
  1.25, 1.12, 1.05, 1.00, 1.00, 1.00
];

// push/alarm steps only — what the dispatcher actually pushes (the silent
// route_start is timeline/calendar-only, never a notification).
export function dispatchableSteps() {
  return AUTO_DAY_SPEC.filter((s) => s.level === "push" || s.level === "alarm");
}

// Every step — the calendar feed shows the whole day, silent steps included.
export function feedSteps() {
  return AUTO_DAY_SPEC.slice();
}

// offsetMinutes for a step given travel + prep — the single place the linear
// coefficients are applied. routeStart + this = the fire minute.
export function stepOffset(step, travelMinutes, prepMinutes) {
  const o = step.offset;
  return o.k + o.travel * travelMinutes + o.prep * prepMinutes;
}
